import React, { useState, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    Fab,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import { useParams, useLocation } from "react-router-dom";

import ProjectDocumentsForm from "./ProjectDocumentsForm";
import { useProjectDocuments } from "../../hooks/useProjectDocuments";
import { useAuthContext } from "../../context/AuthProvider";

const ProjectDocumentsDashboard = () => {
    const { projectId } = useParams();
    const location = useLocation();
    const { pronom, orgnom } = location.state || {};

    const [open, setOpen] = useState(false);
    const [editDocument, setEditDocument] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const { user } = useAuthContext();
    const {
        documents = [],
        isLoading,
        error,
        createMutation,
        updateMutation,
        deleteMutation,
    } = useProjectDocuments(projectId);

    const showAlert = useCallback((message, severity = "success") => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditDocument(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((doc) => {
        setEditDocument(doc);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setOpen(false);
        setEditDocument(null);
    }, []);

    const handleOpenConfirm = useCallback((doc) => {
        setSelectedDocument(doc);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
        setSelectedDocument(null);
    }, []);

    const handleSubmitDocument = useCallback(
        (data) => {
            if (!user) {
                showAlert("Debe iniciar sesión para realizar esta acción", "error");
                return;
            }

            if (editDocument) {
                updateMutation.mutate(
                    {
                        id: editDocument.prodocreqid,
                        data: { ...data, updbyid: user.EmpID, proid: projectId },
                    },
                    {
                        onSuccess: () => {
                            showAlert("Proyecto-Documento actualizado con éxito");
                            handleCloseForm();
                        },
                        onError: (err) => {
                            showAlert(err?.message || "Error al actualizar el Proyecto-Documento", "error");
                        },
                    }
                );
            } else {
                createMutation.mutate(
                    { ...data, proid: projectId, crebyid: user.EmpID },
                    {
                        onSuccess: () => {
                            showAlert("Proyecto-Documento creado con éxito");
                            handleCloseForm();
                        },
                        onError: (err) => {
                            showAlert(err?.message || "Error al crear el Proyecto-Documento", "error");
                        },
                    }
                );
            }
        },
        [user, editDocument, projectId, updateMutation, createMutation, showAlert, handleCloseForm]
    );

    const handleConfirmDelete = useCallback(() => {
        if (!selectedDocument) return;

        deleteMutation.mutate(selectedDocument.prodocreqid, {
            onSuccess: () => {
                showAlert("Proyecto-Documento eliminado con éxito");
                handleCloseConfirm();
            },
            onError: (err) => {
                showAlert(err?.message || "Error al eliminar el Proyecto-Documento", "error");
                handleCloseConfirm();
            },
        });
    }, [selectedDocument, deleteMutation, showAlert, handleCloseConfirm]);

    const normalizedSearch = search.trim().toLowerCase();

    const rows = useMemo(() => {
        let data = documents;

        // Filtro por estado
        if (statusFilter === "A") {
            data = data.filter((d) => d.estreg === "A");
        } else if (statusFilter === "NOT_A") {
            data = data.filter((d) => d.estreg !== "A");
        }

        // Búsqueda
        if (normalizedSearch) {
            data = data.filter((d) => {
                const doctip = (d.doctip || "").toLowerCase();
                const reqent = (d.reqent || "").toLowerCase();
                return doctip.includes(normalizedSearch) || reqent.includes(normalizedSearch);
            });
        }

        return data;
    }, [documents, statusFilter, normalizedSearch]);

    const columns = useMemo(
        () => [
            {
                field: "doctip",
                headerName: "Tipo de Documento",
                flex: 1,
                minWidth: 200,
                valueGetter: (params) => params.row?.doctip || "—",
            },
            {
                field: "reqent",
                headerName: "Entidad",
                width: 150,
                renderCell: (params) => (
                    <Chip
                        label={params.value === "CONDUCTOR" ? "CONDUCTOR" : "VEHÍCULO"}
                        color={params.value === "VEHICULO" ? "primary" : "warning"}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                field: "estreg",
                headerName: "Estado",
                width: 120,
                renderCell: (params) => {
                    const isActive = params.value === "A";
                    return (
                        <Chip
                            label={isActive ? "ACTIVO" : "INACTIVO"}
                            color={isActive ? "success" : "warning"}
                            size="small"
                            variant="outlined"
                        />
                    );
                },
            },
            {
                field: "actions",
                headerName: "Acciones",
                width: 120,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                                <EditIcon color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                            <IconButton
                                size="small"
                                onClick={() => handleOpenConfirm(params.row)}
                                disabled={deleteMutation.isPending}
                            >
                                <DeleteIcon color="error" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [handleOpenEdit, handleOpenConfirm, deleteMutation.isPending]
    );

    if (error) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography color="error">Error al cargar documentos: {error.message}</Typography>
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: 2,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f9f9f9",
            }}
        >
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="h5" fontWeight={700}>
                        Documentación requerida{" "}
                        <Box component="span" sx={{ color: "primary.main" }}>
                            · {pronom}
                        </Box>
                        <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
                            {" "}· {orgnom}
                        </Box>
                    </Typography>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(_, val) => val && setStatusFilter(val)}
                    >
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="A">Activos</ToggleButton>
                        <ToggleButton value="NOT_A">Inactivos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <TextField
                    size="small"
                    label="Buscar por tipo de documento o entidad..."
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <Tooltip title="Limpiar">
                                    <IconButton size="small" onClick={() => setSearch("")}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        getRowId={(row) => row.prodocreqid}
                        disableRowSelectionOnClick
                        pageSizeOptions={[15, 20, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 20, page: 0 } },
                            sorting: { sortModel: [{ field: "doctip", sort: "asc" }] },
                        }}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: "#fff",
                            "& .MuiDataGrid-columnHeaders": {
                                fontWeight: 700,
                                backgroundColor: "#f0f0f0",
                            },
                        }}
                    />
                </Box>
            </Stack>

            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: "fixed", bottom: 20, right: 20 }}
                onClick={handleOpenCreate}
            >
                <AddIcon />
            </Fab>

            {open && (
                <ProjectDocumentsForm
                    open={open}
                    documents={documents}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmitDocument}
                    defaultValues={editDocument || {}}
                />
            )}

            <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el requisito de documento{" "}
                        <strong>{selectedDocument?.doctip}</strong>? Esta operación no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert((a) => ({ ...a, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setAlert((a) => ({ ...a, open: false }))}
                    severity={alert.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ProjectDocumentsDashboard;